import type {
  SchedulePreviewItem,
  ScheduleRunCounts,
  ScheduleRunItemAction,
} from '@nucleus-commerce/api-client/admin';

export interface PreviewFilters {
  storeId: string | null;
  action: ScheduleRunItemAction | null;
}

export interface PreviewGroup {
  storeId: string;
  items: SchedulePreviewItem[];
  /** Per action, for the section header; `reasserted` is not known before a run. */
  counts: ScheduleRunCounts;
}

const COUNT_OF: Record<ScheduleRunItemAction, keyof ScheduleRunCounts> = {
  publish: 'published',
  unpublish: 'unpublished',
  keep: 'kept',
  skip: 'skipped',
  release: 'released',
};

const zero = (): ScheduleRunCounts => ({
  published: 0,
  unpublished: 0,
  kept: 0,
  skipped: 0,
  reasserted: 0,
  released: 0,
});

/**
 * The preview's items by store, in the order the stores come (the schedule's target order, then
 * any other store an item names), filtered by store and action. Items keep the order the
 * backend gave them inside each store.
 */
export function groupPreviewItems(
  items: readonly SchedulePreviewItem[],
  filters: PreviewFilters,
  storeOrder: readonly string[] = [],
): PreviewGroup[] {
  const groups = new Map<string, PreviewGroup>(
    storeOrder.map((storeId) => [storeId, { storeId, items: [], counts: zero() }]),
  );
  for (const item of items) {
    if (filters.storeId && item.storeId !== filters.storeId) continue;
    if (filters.action && item.action !== filters.action) continue;
    let group = groups.get(item.storeId);
    if (!group) {
      group = { storeId: item.storeId, items: [], counts: zero() };
      groups.set(item.storeId, group);
    }
    group.items.push(item);
    group.counts[COUNT_OF[item.action]] += 1;
  }
  return [...groups.values()].filter((group) => group.items.length > 0);
}
